import type { FoodContainer, InventoryEvent, QuantityUnit } from '../domain/model';
import { messageFor, type AppLocale, type MessageKey } from './catalog';
import { demoContentLabel } from './domain-labels';

type Parameters = Readonly<Record<string, string | number>>;
const interpolate = (message: string, parameters: Parameters): string =>
  message.replace(/\{([A-Za-z0-9_]+)\}/g, (placeholder, key: string) =>
    parameters[key] == null ? placeholder : String(parameters[key]));
const translated = (locale: AppLocale, key: MessageKey, parameters: Parameters = {}): string =>
  interpolate(messageFor(locale, key), parameters);

const item = (event: InventoryEvent): FoodContainer | null => event.payload.after ?? event.payload.before;
const reference = (container: FoodContainer, locale: AppLocale): string =>
  translated(locale, container.serialNumber == null ? 'common.itemWithoutNumber' : 'common.number', {
    number: container.serialNumber ?? '',
  });
const unitLabel = (unit: QuantityUnit, quantity: number, locale: AppLocale): string => {
  if (['g', 'kg', 'ml', 'l'].includes(unit)) return translated(locale, `unit.${unit}` as MessageKey);
  const pluralLocale = locale === 'en-XA' ? 'fr-FR' : locale;
  const suffix = new Intl.PluralRules(pluralLocale).select(quantity) === 'one' ? 'one' : 'other';
  return translated(locale, `unit.${unit}.${suffix}` as MessageKey);
};
const dateLabel = (value: string | null, locale: AppLocale): string =>
  value == null
    ? ''
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(value));

export const eventToastMessage = (
  event: InventoryEvent,
  locale: AppLocale,
  demo: boolean,
): string => {
  const container = item(event);
  if (container == null) return translated(locale, 'app.changeSaved');
  const name = demoContentLabel(container.name, locale, demo);
  const after = event.payload.after;
  const before = event.payload.before;
  switch (event.eventType) {
    case 'created_draft':
      return translated(locale, container.numberAssignmentKind === 'reused' ? 'mutation.numberReused' : 'mutation.numberAssigned', { number: container.serialNumber ?? '' });
    case 'confirmed':
      return before == null
        ? translated(locale, 'mutation.fridgeAdded', { name })
        : translated(locale, 'mutation.numberConfirmed', { number: container.serialNumber ?? '' });
    case 'consumed_total':
      return container.trackingMode === 'internal-only'
        ? translated(locale, 'mutation.fridgeConsumed', { name })
        : translated(locale, 'mutation.freezerConsumed', { number: container.serialNumber ?? '', date: dateLabel(after?.numberReleasedAt ?? null, locale) });
    case 'consumed_partial': {
      const quantity = after?.remainingQuantity ?? 0;
      return translated(locale, 'mutation.partial', {
        reference: reference(container, locale),
        quantity: new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(quantity),
        unit: container.unit == null ? '' : unitLabel(container.unit, quantity, locale),
      });
    }
    case 'moved':
      return translated(locale, 'mutation.moved', { reference: reference(container, locale) });
    case 'discarded':
      if (before?.status === 'draft') {
        return translated(locale, 'mutation.additionAbandoned', { number: container.serialNumber ?? '', date: dateLabel(after?.numberReleasedAt ?? null, locale) });
      }
      return container.trackingMode === 'internal-only'
        ? translated(locale, 'mutation.fridgeDiscarded', { name })
        : translated(locale, 'mutation.freezerDiscarded', { number: container.serialNumber ?? '', date: dateLabel(after?.numberReleasedAt ?? null, locale) });
    case 'restored':
      if (before?.serialNumber != null && after?.serialNumber != null && before.serialNumber !== after.serialNumber) {
        return translated(locale, 'mutation.restoredReassigned', { oldNumber: before.serialNumber, number: after.serialNumber });
      }
      return container.trackingMode === 'internal-only'
        ? translated(locale, 'mutation.fridgeRestored', { name })
        : translated(locale, 'mutation.freezerRestored', { number: container.serialNumber ?? '' });
    case 'corrected':
      return translated(locale, 'mutation.corrected', { reference: reference(container, locale) });
    case 'audit_missing':
      return translated(locale, 'mutation.auditMissing', { number: container.serialNumber ?? '' });
    case 'audit_confirmed':
      return translated(locale, 'mutation.auditConfirmed', { number: container.serialNumber ?? '' });
    case 'undo':
      if (before?.serialNumber != null && after?.serialNumber != null && before.serialNumber !== after.serialNumber) {
        return translated(locale, 'mutation.undoReassigned', { oldNumber: before.serialNumber, number: after.serialNumber });
      }
      return after?.serialNumber != null
        ? translated(locale, 'mutation.undoRestored', { number: after.serialNumber })
        : translated(locale, 'mutation.undo');
    case 'incident_changed':
      return translated(locale, 'app.changeSaved');
  }
};
